import React, { useState, useRef, useEffect } from 'react';
import { MoreHorizontal, X, Loader2, CalendarIcon, Info } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar } from '../ui/calendar';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import FileUploadSection from './FileUploadSection';
import { inputClass, labelClass } from './vaultUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OthersEntryModal = ({ mode, entry, onClose, onSave, saving }) => {
  // Parse existing expiry date if present
  const parseExpiryDate = () => {
    if (entry?.data?.expiry_date) {
      try {
        return parseISO(entry.data.expiry_date);
      } catch {
        return null;
      }
    }
    return null;
  };

  const [formData, setFormData] = useState({
    save_as: entry?.save_as || '',
    important_information: entry?.data?.important_information || '',
    expiry_date: entry?.data?.expiry_date || ''
  });
  const [expiryDate, setExpiryDate] = useState(parseExpiryDate());
  const [calendarOpen, setCalendarOpen] = useState(false);
  
  // Admin-configured settings
  const [expiryEnabled, setExpiryEnabled] = useState(true);
  const [reminderMonths, setReminderMonths] = useState(6);

  const [files, setFiles] = useState(entry?.files || []);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Fetch admin settings for expiry date feature
  useEffect(() => {
    const fetchExpirySettings = async () => {
      try {
        const response = await fetch(`${API_URL}/api/client-features/document-expiry`);
        if (response.ok) {
          const data = await response.json();
          setExpiryEnabled(data.enabled);
          setReminderMonths(data.reminder_months);
        }
      } catch (error) {
        console.error('Error fetching expiry settings:', error);
      }
    };
    fetchExpirySettings();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleExpiryDateSelect = (date) => {
    setExpiryDate(date);
    if (date) {
      // Store as ISO string for backend
      setFormData(prev => ({ ...prev, expiry_date: date.toISOString() }));
    } else {
      setFormData(prev => ({ ...prev, expiry_date: '' }));
    }
    setCalendarOpen(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.save_as.trim()) {
      toast.error('Please enter a name for the document');
      return;
    }
    onSave(formData, pendingFiles);
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="others-entry-modal">
      <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-slate-500/10">
              <MoreHorizontal className="w-5 h-5 text-slate-400" />
            </div>
            <h2 className="text-xl font-bold text-white">
              {mode === 'add' ? 'Add Other Document' : 'Edit Document'}
            </h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-2 hover:bg-slate-800 rounded-lg">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-180px)] space-y-6">
          <FileUploadSection 
            mode={mode} 
            entry={entry} 
            files={files} 
            setFiles={setFiles} 
            pendingFiles={pendingFiles} 
            setPendingFiles={setPendingFiles} 
            uploading={uploading} 
            setUploading={setUploading} 
            fileInputRef={fileInputRef} 
          />

          <div className="space-y-2">
            <Label className={labelClass}>Name of Document *</Label>
            <Input name="save_as" value={formData.save_as} onChange={handleChange} placeholder="e.g., Passport, PAN Card, Aadhaar" className={inputClass} />
          </div>

          {/* Expiry Date Field - Only show if enabled by admin */}
          {expiryEnabled && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label className={labelClass}>Expiry Date</Label>
              <div className="group relative">
                <Info size={14} className="text-slate-500 cursor-help" />
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-slate-800 text-slate-200 text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 border border-slate-700 shadow-lg">
                  You&apos;ll receive a reminder {reminderMonths} month{reminderMonths > 1 ? 's' : ''} before expiry
                </div>
              </div>
            </div>
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={`w-full justify-start text-left font-normal ${inputClass} ${
                    !expiryDate && 'text-slate-500'
                  }`}
                  data-testid="expiry-date-picker"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {expiryDate ? format(expiryDate, 'dd MMM yyyy') : 'Select expiry date (optional)'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-slate-900 border-slate-700" align="start">
                <Calendar
                  mode="single"
                  selected={expiryDate}
                  onSelect={handleExpiryDateSelect}
                  initialFocus
                  fixedWeeks
                  disabled={(date) => date < new Date()}
                  className="rounded-md bg-slate-900"
                  classNames={{
                    months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                    month: "space-y-4 w-[280px]",
                    caption: "flex justify-center pt-1 relative items-center h-10",
                    caption_label: "text-base font-semibold text-white",
                    nav: "space-x-1 flex items-center",
                    nav_button: "h-8 w-8 bg-slate-800 hover:bg-slate-700 text-white rounded-md flex items-center justify-center border border-slate-600 hover:border-slate-500 transition-colors",
                    nav_button_previous: "absolute left-1",
                    nav_button_next: "absolute right-1",
                    table: "w-full border-collapse",
                    head_row: "flex justify-between",
                    head_cell: "text-slate-400 rounded-md w-9 font-medium text-[0.8rem] text-center",
                    row: "flex w-full mt-2 justify-between",
                    cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-amber-500/20 [&:has([aria-selected])]:rounded-md",
                    day: "h-9 w-9 p-0 font-normal text-slate-200 hover:bg-slate-700 hover:text-white rounded-md transition-colors flex items-center justify-center",
                    day_selected: "bg-amber-500 text-slate-900 hover:bg-amber-400 hover:text-slate-900 font-semibold",
                    day_today: "bg-slate-700 text-amber-400 font-semibold",
                    day_outside: "text-slate-600 opacity-50",
                    day_disabled: "text-slate-600 opacity-30 cursor-not-allowed hover:bg-transparent",
                    day_hidden: "invisible",
                  }}
                />
                {expiryDate && (
                  <div className="p-2 border-t border-slate-700">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleExpiryDateSelect(null)}
                      className="w-full text-slate-400 hover:text-white hover:bg-slate-800"
                    >
                      Clear date
                    </Button>
                  </div>
                )}
              </PopoverContent>
            </Popover>
            {expiryDate && (
              <p className="text-xs text-emerald-400 flex items-center gap-1">
                <CalendarIcon size={12} />
                Reminder will be sent {reminderMonths} month{reminderMonths > 1 ? 's' : ''} before expiry
              </p>
            )}
          </div>
          )}

          <div className="space-y-2">
            <Label className={labelClass}>Important Information</Label>
            <textarea
              name="important_information"
              value={formData.important_information}
              onChange={handleChange}
              rows="4"
              placeholder="Any relevant details, notes, or instructions about this document..."
              className={`${inputClass} w-full rounded-md p-2`}
            ></textarea>
          </div>
        </form>

        <div className="p-6 border-t border-slate-800 flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={onClose} className="border-slate-600 text-slate-300 hover:bg-slate-800">Cancel</Button>
          <Button type="submit" onClick={handleSubmit} disabled={saving} className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold">
            {saving ? <><Loader2 size={16} className="mr-2 animate-spin" /> Saving...</> : mode === 'add' ? 'Add Entry' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OthersEntryModal;
